import React, { useState } from "react";
import { CaaSImageFilters } from "@app/types/OptimisedImage";
import { BlockTitle } from "@blocks/Blocks";
import Ad from "@components/Advertisement/Ad";
import AdSettings from "@components/Advertisement/AdSettings";
import Container from "@components/Container/Container";
import GAData from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import MetaTags from "@components/MetaTags/MetaTags";
import OptimisedImage from "@components/OptimisedImage";
import EventsAwardsFooter from "@events-awards/components/EventsAwardsFooter";
import { ImageModal } from "@events-awards/components/EventsAwardsGalleryImageModal";
import EventsAwardsLogoMenu from "@events-awards/components/EventsAwardsLogoMenu";
import { AWARDS_EVENTS_PAGE_ROW_DATA } from "@events-awards/utils/constants";
import { getAllImages } from "@events-awards/utils/helpers";
import {
  AwardDataRow,
  EventsAwardsGalleryContextData,
} from "@events-awards/utils/types";
import { useDetectAdBlock } from "adblock-detect-react";

import EventsAwardsHeader from "./EventAwardsHeader";

/**
 * Component to render gallery year page for event and awards
 */
const EventsAwardsGalleryYearPageContent = ({
  title,
  articles,
  awardType,
}: EventsAwardsGalleryContextData): React.ReactElement => {
  const adBlockDetected = useDetectAdBlock();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const awardData: AwardDataRow | undefined = AWARDS_EVENTS_PAGE_ROW_DATA.find(
    (data) => data.awardType === awardType
  );
  const awardPath = awardData?.blockTitleProps?.link || "";
  const allImages = getAllImages(articles);
  const imageFilters: CaaSImageFilters[] = [
    { w: 269, h: 202, dpr: 1, f: "webp" },
  ];

  const handleImageClick = (imageUrl: string): void => {
    setSelectedImage(imageUrl);
    setModalOpen(true);
  };

  return (
    <div
      className="min-h-screen w-full bg-gray-50"
      data-testid="events-gallery-year-page-component"
    >
      <AdSettings
        adNames={["lb1", "prestitial", "catfish", "abm"]}
        path={`${awardPath}/gallery`}
        adTargets={[{ key: "page", value: "listing" }]}
      />

      <GAData
        title={`${awardData?.blockTitleProps?.text} Gallery`}
        level2="events awards"
        adblocker={adBlockDetected ? 1 : 0}
      />

      <MetaTags
        title={title}
        description={`The Business Times ${awardData?.blockTitleProps?.text} - ${awardData?.description}`}
        ogType="website"
        slug={awardPath}
      />

      <EventsAwardsHeader />

      <Container>
        <Row rootClassName="justify-center">
          <Column rootClassName="w-full mb-8">
            <EventsAwardsLogoMenu awardType={awardType} />

            <div className="py-8">
              <BlockTitle
                text={articles?.[0].title}
                rootClassName="text-5xl md:text-8xl lg:text-11xl font-medium text-left mb-4 font-public-sans"
              />

              <div className="grid grid-cols-2 gap-6 md:grid-cols-2 lg:grid-cols-4">
                {allImages.map((image) => {
                  const imageUrl =
                    image.relation?.fields?.["landscape-caas"]?.url;

                  if (typeof imageUrl === "undefined") return <></>;

                  return (
                    <div
                      key={image.id}
                      className="cursor-pointer overflow-hidden bg-white shadow-sm"
                      onClick={() => {
                        handleImageClick(imageUrl);
                      }}
                    >
                      <OptimisedImage
                        src={imageUrl}
                        loading="lazy"
                        alt={
                          image.fields?.find((f) => f.name === "caption")
                            ?.value || ""
                        }
                        filters={imageFilters}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </Column>
        </Row>
      </Container>

      <EventsAwardsFooter awardType={awardType} />

      <ImageModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        image={selectedImage}
      />

      <Ad adType="catfish" />

      <Ad adType="abm" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default EventsAwardsGalleryYearPageContent;
